import React from 'react';
import { Box, Container, Typography, Grid, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const FooterWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#1A365D',
  color: '#fff',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: 'linear-gradient(45deg, rgba(0,75,135,0.1) 0%, rgba(0,75,135,0) 100%)',
    transform: 'skewY(-6deg)',
    transformOrigin: 'top left',
  }
}));

const FooterContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  padding: '80px 0 40px',
  zIndex: 1,
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 500,
  marginBottom: '1.5rem',
  color: '#fff',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-8px',
    left: 0,
    width: '40px',
    height: '2px',
    background: '#004B87',
  }
}));

const FooterLink = styled(Box)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.8)',
  textDecoration: 'none',
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
  cursor: 'pointer',
  '&:hover': {
    color: '#fff',
    transform: 'translateX(4px)',
  }
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  color: '#fff',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  marginRight: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#004B87',
    transform: 'translateY(-2px)',
  }
}));

const Copyright = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: 'rgba(255, 255, 255, 0.6)',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  marginTop: '40px',
  paddingTop: '20px',
  fontSize: '0.875rem',
}));

const Footer = () => {
  const navigate = useNavigate();

  return (
    <FooterWrapper>
      <FooterContainer 
        maxWidth="xl"
        sx={{
          px: { xs: 2, sm: 4, md: 8, lg: 12 },
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <FooterTitle variant="h6">À propos</FooterTitle>
            <Typography 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                marginBottom: '1rem',
                lineHeight: 1.8,
                fontSize: '1rem'
              }}
            >
              Le groupe L2MI et Airmetrologie est un groupe de laboratoires de métrologie de référence, offrant des services d'étalonnage et d'essais de haute précision.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <SocialButton 
                component="a" 
                href="https://www.linkedin.com/" 
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkedInIcon />
              </SocialButton>
              <SocialButton 
                component="a" 
                href="mailto:contact@l2mi.com"
              >
                <EmailIcon />
              </SocialButton>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FooterTitle variant="h6">Nos Laboratoires</FooterTitle>
            <FooterLink onClick={() => navigate('/who-we-are')}>Qui sommes-nous</FooterLink>
            <FooterLink onClick={() => navigate('/secteurs-l2mi')}>Laboratoire L2MI</FooterLink>
            <FooterLink onClick={() => navigate('/secteurs-air-metrologie')}>Laboratoire Air Métrologie</FooterLink>
            <FooterLink onClick={() => navigate('/accreditations')}>Accréditations</FooterLink>
            <FooterTitle variant="h6" sx={{ mt: 3 }}>Services</FooterTitle>
            <FooterLink onClick={() => navigate('/reclamation')}>Réclamation</FooterLink>
            <FooterLink onClick={() => navigate('/carrieres')}>Carrières</FooterLink>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FooterTitle variant="h6">Contact Air Métrologie</FooterTitle>
            <FooterLink 
              component="a" 
              href="tel:+212522350643" 
              sx={{ marginBottom: 1 }}
            >
              <PhoneIcon sx={{ fontSize: 20 }} />
              +212 5 22 35 06 43
            </FooterLink>
            <FooterLink 
              component="a" 
              href="mailto:a.metrologie@gmail.com" 
              sx={{ marginBottom: 1 }}
            >
              <EmailIcon sx={{ fontSize: 20 }} />
              a.metrologie@gmail.com
            </FooterLink>
            <FooterLink 
              component="a" 
              
              target="_blank"
              rel="noopener noreferrer"
              sx={{ marginBottom: 1 }}
            >
              <LocationOnIcon sx={{ fontSize: 20 }} />
              Bd Chefchaouni KM 8.5 Porte B, 1er Etage Appt 3 Aîn Sebâa - Casablanca
            </FooterLink>
            <FooterTitle variant="h6" sx={{ mt: 3 }}>Politique Qualité</FooterTitle>
            <FooterLink onClick={() => navigate('/politique-qualite-airmetrologie')}>Air Métrologie</FooterLink>
            <FooterLink onClick={() => navigate('/politique-qualite-l2mi')}>L2MI</FooterLink>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FooterTitle variant="h6">Contact L2MI</FooterTitle>
            <FooterLink 
              component="a" 
              href="tel:+212673868680" 
              sx={{ marginBottom: 1 }}
            >
              <PhoneIcon sx={{ fontSize: 20 }} />
              +212 673 868 680
            </FooterLink>
            <FooterLink 
              component="a" 
              href="mailto:l2mi2008@gmail.com" 
              sx={{ marginBottom: 1 }}
            >
              <EmailIcon sx={{ fontSize: 20 }} />
              l2mi2008@gmail.com
            </FooterLink>
            <FooterLink 
              component="a" 
              target="_blank"
              rel="noopener noreferrer"
              sx={{ marginBottom: 1 }}
            >
              <LocationOnIcon sx={{ fontSize: 20 }} />
              Casablanca lot N° 18 Quartier Industriel Mauritania 4ème étage, Sidi Bernoussi-Maroc
            </FooterLink>
            
          </Grid>
        </Grid>

        <Copyright>
          {new Date().getFullYear()} L2MI - Laboratoire de Métrologie. Tous droits réservés.
        </Copyright>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default Footer;
